import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import API_URL from '../config';

// Set up axios to always send the token
const getStoredToken = () => localStorage.getItem('indie_law_token');
const setStoredToken = (token) => {
  if (token) localStorage.setItem('indie_law_token', token);
  else localStorage.removeItem('indie_law_token');
};

// Store preview token globally so interceptor can use it
let activePreviewToken = null;

// Axios interceptor to add auth header to all requests
axios.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    // Use preview token if in preview mode, otherwise use stored token
    const token = activePreviewToken || getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Helper to format API error details
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);

  const checkAuth = useCallback(async () => {
    // Check for preview token in URL
    const params = new URLSearchParams(window.location.search);
    const previewToken = params.get('preview_token');
    
    if (previewToken) {
      activePreviewToken = previewToken;
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${previewToken}` }
        });
        setUser(response.data);
        setPreviewMode(true);
      } catch (error) {
        setUser(false);
      } finally {
        setLoading(false);
      }
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Token might be expired, try refresh
      const refreshToken = localStorage.getItem('indie_law_refresh');
      if (refreshToken) {
        try {
          const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });
          if (refreshRes.data.access_token) {
            setStoredToken(refreshRes.data.access_token);
            const meRes = await axios.get(`${API_URL}/api/auth/me`);
            setUser(meRes.data);
          } else {
            setStoredToken(null);
            setUser(false);
          }
        } catch (refreshError) {
          setStoredToken(null);
          localStorage.removeItem('indie_law_refresh');
          setUser(false);
        }
      } else {
        setStoredToken(null);
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth — LoginPage exchanges the session_id and sets the token.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const data = response.data;
      
      // Store tokens
      if (data.access_token) {
        setStoredToken(data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('indie_law_refresh', data.refresh_token);
      }
      
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const loginWithGoogle = async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/google/session`, { session_id: sessionId });
      const data = response.data;
      if (data.access_token) setStoredToken(data.access_token);
      if (data.refresh_token) localStorage.setItem('indie_law_refresh', data.refresh_token);
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setStoredToken(null);
      localStorage.removeItem('indie_law_refresh');
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    checkAuth,
    previewMode,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
